import { useEffect, useRef, useState } from 'react';
import { useApp } from '../store';
import { promptDialog } from '../confirm';
import { displayName } from '../people';
import VendorTag from './VendorTag';
import { asDate } from '../utils';
import { can } from '../permissions';
import Icon, { type IconName } from './Icon';
import NavIcon from './NavIcon';
import InfoTip from './InfoTip';
import DateField from './DateField';
import RichTextEditor from './RichTextEditor';
import { EMPTY_RICH_DOC, richHasText, richText, type RichDoc } from '../rich-text';
import {
  DECISION_ORIGIN_LABELS,
  canSupersede,
  isLiveDecision,
  liveDecisions,
  supersedeChain,
  type Decision,
  type DecisionDraft,
  type DecisionLink,
} from '../decisions';

/**
 * THE DECISIONS TAB — 3.19 Learning & Governance spine, D1 (the view).
 *
 * The record of the calls a project made — the choice, who made it, when, and
 * what it changed — so a settled question is not re-litigated and the reasoning
 * is not lost. Built to the approved mock (dev-preview/decision-log): the live
 * log newest-first as `dcard`s, an "Include superseded" toggle that reveals the
 * supersede chain (the muted original with "Superseded by …", the replacement
 * carrying "Replaces …"), the example-first empty state, and the R17 log/edit/
 * supersede form.
 *
 * The pure layer (../decisions) owns the shape and the rules; the store owns the
 * writes (logDecision / editDecision / decideDecision / supersedeDecision /
 * withdrawDecision), each returning '' on success or a message to surface.
 */

/** A link chip's glyph, by the Reveal target kind it points at. */
function linkIcon(kind: string): IconName {
  switch (kind) {
    case 'signoff':
      return 'signature';
    case 'change-request':
      return 'document';
    case 'budget':
      return 'coin';
    case 'scope':
      return 'flag';
    default:
      return 'task';
  }
}

/** The quiet origin glyph — a decision composed from the board reads where it came from. */
function originIcon(origin: Decision['origin']): IconName {
  switch (origin) {
    case 'signoff':
      return 'signature';
    case 'change-request':
      return 'document';
    case 'blocker':
      return 'blocked';
    default:
      return 'pencil';
  }
}

/** A ms instant as the `yyyy-mm-dd` a date input wants; absent = today. Read as
 *  LOCAL midnight so the day never slips in a western timezone. */
function dayString(ms?: number): string {
  const d = ms ? new Date(ms) : new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** A `yyyy-mm-dd` back to a ms instant at LOCAL midnight; empty = now. */
function dayToMs(s: string): number {
  if (!s) return Date.now();
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return Date.now();
  return new Date(y, m - 1, d).getTime();
}

/** The links a decision affected, as Reveal chips — click one to open the card,
 *  document or screen it points at. (The manual log form seeds no links; the
 *  derive-first entry points carry them in.) */
function LinkChips({ links }: { links?: DecisionLink[] }) {
  const revealLink = useApp((s) => s.revealLink);
  if (!links || links.length === 0) return null;
  return (
    <span className="dlinks">
      <span className="dlinks-lab">Affected</span>
      {links.map((l) => (
        <button
          type="button"
          key={`${l.kind}:${l.id}`}
          className="lchip"
          onClick={() => revealLink(l)}
          title={`Open ${l.label}`}
        >
          <Icon name={linkIcon(l.kind)} size={13} />
          {l.label}
        </button>
      ))}
    </span>
  );
}

export default function DecisionsView() {
  const decisions = useApp((s) => s.decisions);
  const decisionsLoaded = useApp((s) => s.decisionsLoaded);
  const profiles = useApp((s) => s.profiles);
  const projectName = useApp((s) => s.meta.name);
  const user = useApp((s) => s.user);
  const role = useApp((s) => s.role);
  // A writer (rank 3+) may log, edit, supersede or withdraw — the same bar the
  // decisions RLS enforces server-side. A viewer sees the log, not the controls.
  const mayWrite = can(role, 'editIssue');
  const logDecision = useApp((s) => s.logDecision);
  const editDecision = useApp((s) => s.editDecision);
  const decideDecision = useApp((s) => s.decideDecision);
  const supersedeDecision = useApp((s) => s.supersedeDecision);
  const withdrawDecision = useApp((s) => s.withdrawDecision);

  const [showAll, setShowAll] = useState(false);
  // The form overlay: 'log' a new one, 'edit' an open draft in place, or
  // 'supersede' a decided one with a replacement. null = the list.
  const [form, setForm] = useState<
    | null
    | { mode: 'log' }
    | { mode: 'edit'; decision: Decision }
    | { mode: 'supersede'; decision: Decision }
  >(null);
  // "View the replacement" flashes the live replacement card so the reader's eye
  // lands on it, the same reveal the Plan uses.
  const [flashId, setFlashId] = useState<string | null>(null);
  useEffect(() => {
    if (!flashId) return;
    const el = document.getElementById(`decision-${flashId}`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.classList.add('just-revealed');
    const t = window.setTimeout(() => {
      el?.classList.remove('just-revealed');
      setFlashId(null);
    }, 1800);
    return () => window.clearTimeout(t);
  }, [flashId]);

  // Reveal a decision anywhere in the log. A superseded or withdrawn target sits in
  // a band shown only under "Show all", so open that first, then flash it — otherwise
  // the link would point at a card that isn't in the DOM.
  const reveal = (id: string) => {
    const target = decisions.find((d) => d.id === id);
    if (target && !isLiveDecision(target)) setShowAll(true);
    setFlashId(id);
  };

  const live = liveDecisions(decisions);
  const superseded = decisions
    .filter((d) => d.status === 'superseded')
    .sort((a, b) => b.createdAt - a.createdAt);
  const withdrawn = decisions
    .filter((d) => d.status === 'withdrawn')
    .sort((a, b) => (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt));

  if (form) {
    return (
      <DecisionForm
        key={form.mode + (form.mode === 'log' ? '' : form.decision.id)}
        mode={form.mode}
        decision={form.mode === 'log' ? undefined : form.decision}
        projectName={projectName}
        me={user?.email ?? ''}
        profiles={profiles}
        onClose={() => setForm(null)}
        logDecision={logDecision}
        editDecision={editDecision}
        decideDecision={decideDecision}
        supersedeDecision={supersedeDecision}
      />
    );
  }

  const card = (d: Decision) => {
    const chain = supersedeChain(d, decisions);
    const replaces = chain.length ? chain[chain.length - 1] : null;
    const decided = d.status === 'decided';
    const whoEmail = decided ? d.decidedBy || d.madeBy : d.madeBy;
    const who = displayName(whoEmail, profiles);
    const when = asDate(decided ? d.decidedAt ?? d.createdAt : d.createdAt);
    return (
      <div className="dcard" id={`decision-${d.id}`} key={d.id}>
        {replaces && (
          <div className="chain">
            <NavIcon view="decisions" size={15} />
            <span>
              Replaces&nbsp;<b>&ldquo;{replaces.title}&rdquo;</b> ·{' '}
              <button className="link" onClick={() => reveal(replaces.id)}>
                view the original
              </button>
            </span>
          </div>
        )}
        <div className="dtop">
          <span className={`rag ${decided ? 'g' : 'a'}`}>
            <span className="dot" />
            {decided ? 'Decided' : 'Open'}
          </span>
          <h3 className="dtitle">{d.title}</h3>
          <span className="dorigin">
            <Icon name={originIcon(d.origin)} size={13} />
            {DECISION_ORIGIN_LABELS[d.origin]}
          </span>
        </div>
        {richHasText(d.detail) && <p className="ddetail">{richText(d.detail)}</p>}
        <div className="dmeta">
          <span className="dwho">
            {decided ? (
              <>
                Decided by <b>{who}</b><VendorTag who={whoEmail} profiles={profiles} compact /> · {when}
              </>
            ) : (
              <>
                Awaiting a decision · raised by <b>{who}</b><VendorTag who={whoEmail} profiles={profiles} compact /> · {when}
              </>
            )}
          </span>
          <LinkChips links={d.links} />
          {mayWrite && (
            <span className="dcard-acts">
              {d.status === 'open' && (
                <button className="link" onClick={() => setForm({ mode: 'edit', decision: d })}>
                  Edit
                </button>
              )}
              {canSupersede(d) && (
                <button className="link" onClick={() => setForm({ mode: 'supersede', decision: d })}>
                  Supersede
                </button>
              )}
              <button
                className="link"
                onClick={async () => {
                  const reason = await promptDialog({
                    title: 'Withdraw this decision?',
                    body: (
                      <>
                        “<b>{d.title}</b>” stays in the record but drops out of the log. Say why —
                        it’s kept with the decision.
                      </>
                    ),
                    label: 'Why it’s being withdrawn',
                    placeholder: 'e.g. Superseded by the region-phasing call; no longer our approach.',
                    required: true,
                    submitLabel: 'Withdraw',
                  });
                  if (reason === null) return;
                  void withdrawDecision(d.id, reason);
                }}
              >
                Withdraw
              </button>
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="gov">
      <div className="gov-head">
        <div className="gov-head-l">
          <div className="gov-title-row">
            <h1 className="fd-title">Decisions</h1>
            <span className="ihint">
              <InfoTip label="Decisions">
                The record of the calls this project made — the choice, who made it, when, and what
                it changed — so a settled question is not reopened and the reasoning is not lost. Log
                one by hand, or confirm one the board offers from a cleared blocker, a sign-off or a
                change request.
              </InfoTip>
            </span>
          </div>
          <p className="gov-sub">
            {projectName} · what was decided, why, and what it changed · newest first
          </p>
        </div>
        <div className="gov-tools">
          {decisions.length > 0 && (superseded.length > 0 || withdrawn.length > 0) && (
            <div className="seg">
              <button
                className={`seg-btn ${!showAll ? 'active' : ''}`}
                onClick={() => setShowAll(false)}
              >
                Current
              </button>
              <button
                className={`seg-btn ${showAll ? 'active' : ''}`}
                onClick={() => setShowAll(true)}
              >
                Show all
              </button>
            </div>
          )}
          {mayWrite && (
            <button className="btn btn-primary" onClick={() => setForm({ mode: 'log' })}>
              <Icon name="plus" size={15} />
              Log a decision
            </button>
          )}
        </div>
      </div>
      <div className="gov-rule" />

      {decisions.length === 0 ? (
        !decisionsLoaded ? (
          <p className="gov-none">Loading the decision log…</p>
        ) : (
        <div className="gov-empty">
          <div className="ge-mark">
            <NavIcon view="decisions" size={24} />
          </div>
          <h2>No decisions logged yet</h2>
          <p>
            The decision log is the record of the calls this project made — the choice, who made it,
            when, and what it changed — so a settled question is not reopened and the reasoning
            behind it is not lost.
          </p>
          <div className="how">
            <p className="how-h">To log a decision</p>
            <div className="how-step">
              <Icon name="plus" size={15} />
              <span>
                Click <b>Log a decision</b>
              </span>
            </div>
            <div className="how-step">
              <NavIcon view="decisions" size={15} />
              <span>Say what was decided and why, in a line or two</span>
            </div>
            <div className="how-step">
              <Icon name="link" size={15} />
              <span>Link the card, scope or change request it affected</span>
            </div>
          </div>
          <p style={{ marginTop: 16 }}>
            Decisions also land here on their own: clearing a <b>decision</b> blocker, settling a{' '}
            <b>sign-off</b>, or promoting a <b>change-request</b> assumption each offer to log the
            call — pre-filled, so you confirm rather than retype.
          </p>
          {mayWrite && (
            <div className="btn-row">
              <button className="btn btn-primary" onClick={() => setForm({ mode: 'log' })}>
                <Icon name="plus" size={15} />
                Log a decision
              </button>
            </div>
          )}
        </div>
        )
      ) : (
        <>
          {live.length > 0 ? (
            live.map(card)
          ) : (
            <p className="gov-none">
              No current decisions —{' '}
              {showAll
                ? 'the superseded and withdrawn ones are below.'
                : 'switch to Show all to see superseded and withdrawn ones.'}
            </p>
          )}

          {showAll && superseded.length > 0 && (
            <>
              <div className="band">
                <span className="band-h">Superseded</span>
                <span className="band-c">{superseded.length}</span>
              </div>
              {superseded.map((d) => {
                const replacement = decisions.find((x) => x.supersedes === d.id);
                return (
                  <div className="dcard muted" id={`decision-${d.id}`} key={d.id}>
                    <div className="dtop">
                      <span className="rag n">
                        <span className="dot" />
                        Superseded
                      </span>
                      <h3 className="dtitle">{d.title}</h3>
                      <span className="dorigin">
                        <Icon name={originIcon(d.origin)} size={13} />
                        {DECISION_ORIGIN_LABELS[d.origin]}
                      </span>
                    </div>
                    {richHasText(d.detail) && <p className="ddetail">{richText(d.detail)}</p>}
                    {replacement && (
                      <div className="supnote">
                        <Icon name="chevron" size={14} />
                        <span>
                          Superseded by <b>&ldquo;{replacement.title}&rdquo;</b> ·{' '}
                          <button className="link" onClick={() => reveal(replacement.id)}>
                            view the replacement
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {showAll && withdrawn.length > 0 && (
            <>
              <div className="band">
                <span className="band-h">Withdrawn</span>
                <span className="band-c">{withdrawn.length}</span>
              </div>
              {withdrawn.map((d) => (
                <div className="dcard muted" id={`decision-${d.id}`} key={d.id}>
                  <div className="dtop">
                    <span className="rag n">
                      <span className="dot" />
                      Withdrawn
                    </span>
                    <h3 className="dtitle">{d.title}</h3>
                    <span className="dorigin">
                      <Icon name={originIcon(d.origin)} size={13} />
                      {DECISION_ORIGIN_LABELS[d.origin]}
                    </span>
                  </div>
                  {richHasText(d.detail) && <p className="ddetail">{richText(d.detail)}</p>}
                  {d.withdrawnReason && (
                    <div className="supnote">
                      <Icon name="close" size={14} />
                      <span>
                        Withdrawn — {d.withdrawnReason}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ── The log / edit / supersede form (R17) ──────────────────────────────────── */

function DecisionForm({
  mode,
  decision,
  projectName,
  me,
  profiles,
  onClose,
  logDecision,
  editDecision,
  decideDecision,
  supersedeDecision,
}: {
  mode: 'log' | 'edit' | 'supersede';
  decision?: Decision;
  projectName: string;
  me: string;
  profiles: ReturnType<typeof useApp.getState>['profiles'];
  onClose: () => void;
  logDecision: (draft: DecisionDraft) => Promise<string>;
  editDecision: (id: string, patch: { title?: string; detail?: RichDoc }) => Promise<string>;
  decideDecision: (id: string, when?: { decidedBy?: string; decidedAt?: number }) => Promise<string>;
  supersedeDecision: (id: string, draft: DecisionDraft) => Promise<string>;
}) {
  // Edit loads the open draft's own text; supersede starts the replacement blank
  // (the new call, not the old words); log starts empty.
  const seed = mode === 'edit' ? decision : undefined;
  const [title, setTitle] = useState(seed?.title ?? '');
  const [detail, setDetail] = useState<RichDoc>(seed?.detail ?? EMPTY_RICH_DOC);
  // An edited open draft keeps its 'open' status until you move it on; a brand-new
  // one defaults to Decided; a supersede is always a decided replacement.
  const [status, setStatus] = useState<'open' | 'decided'>(
    mode === 'supersede' ? 'decided' : mode === 'edit' ? decision?.status === 'open' ? 'open' : 'decided' : 'decided',
  );
  // Who made the call and when — editable, because a decision is often logged
  // after the fact for a call someone else made. Default to me + today.
  const [decidedBy, setDecidedBy] = useState(seed?.decidedBy ?? me);
  const [decidedOn, setDecidedOn] = useState(dayString(seed?.decidedAt));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);
  useEffect(() => titleRef.current?.focus(), []);
  // The people a decision can be attributed to — everyone known, me first.
  const people = (() => {
    const emails = Object.keys(profiles);
    const meKey = me.trim().toLowerCase();
    return meKey && !emails.includes(meKey) ? [meKey, ...emails] : emails;
  })();

  const heading =
    mode === 'edit' ? 'Edit the decision' : mode === 'supersede' ? 'Supersede the decision' : 'Log a decision';
  const sub =
    mode === 'supersede'
      ? `${projectName} · a new call that replaces an earlier one · drafted just now`
      : `${projectName} · a decision and why it was made · drafted just now`;
  const submitLabel = mode === 'edit' ? 'Save changes' : mode === 'supersede' ? 'Supersede' : 'Log decision';

  const submit = async () => {
    if (!title.trim()) {
      setProblem('Give the decision a title.');
      return;
    }
    setBusy(true);
    setProblem('');
    // Who/when only travel on a DECIDED decision; an open draft carries neither.
    const when = status === 'decided' ? { decidedBy, decidedAt: dayToMs(decidedOn) } : {};
    let err = '';
    if (mode === 'log') {
      err = await logDecision({ title, detail, status, origin: 'manual', ...when });
    } else if (mode === 'supersede' && decision) {
      err = await supersedeDecision(decision.id, {
        title,
        detail,
        status: 'decided',
        origin: decision.origin,
        decidedBy,
        decidedAt: dayToMs(decidedOn),
      });
    } else if (mode === 'edit' && decision) {
      err = await editDecision(decision.id, { title, detail });
      // Moving an open draft on to Decided is a second action — carry the chosen
      // decider/date (default me + today) rather than always stamping now.
      if (!err && status === 'decided' && decision.status === 'open') {
        err = await decideDecision(decision.id, { decidedBy, decidedAt: dayToMs(decidedOn) });
      }
    }
    setBusy(false);
    if (err) {
      setProblem(err);
      return;
    }
    onClose();
  };

  return (
    <div className="gov">
      <div className="fd-wrap">
        <div className="fd-head">
          <div className="fd-head-main">
            <h1 className="fd-title">{heading}</h1>
            <p className="fd-sub">{sub}</p>
          </div>
          <div className="fd-head-side">
            <span className={`rag ${status === 'decided' ? 'g' : 'a'}`}>
              <span className="dot" />
              {status === 'decided' ? 'Decided' : 'Open'}
            </span>
          </div>
        </div>
        <div className="fd-form">
          {mode === 'supersede' && decision && (
            <div className="chain fd-replacing">
              <NavIcon view="decisions" size={15} />
              <span>
                Replacing&nbsp;<b>&ldquo;{decision.title}&rdquo;</b>
              </span>
            </div>
          )}
          <div className="fd-sec">
            <h2 className="fd-sec-h">The decision</h2>
            <div className="fd-grid">
              <div className="fd-field fd-wide">
                <label className="fd-lab">
                  Decision<span className="req">*</span>
                </label>
                <input
                  ref={titleRef}
                  className="input"
                  aria-required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="The call that was made, in a line"
                />
              </div>
              <div className="fd-field fd-wide">
                <label className="fd-lab">Why, and what it changed</label>
                <RichTextEditor
                  value={detail}
                  onChange={setDetail}
                  ariaLabel="Why, and what it changed"
                  placeholder="e.g. Capped the first release to one region to protect the Go-Live date."
                />
                <p className="hintline">
                  The reasoning a future reader needs — format it as you like; not the meeting transcript.
                </p>
              </div>
            </div>
          </div>

          {mode !== 'supersede' && (
            <div className="fd-sec">
              <h2 className="fd-sec-h">Status</h2>
              <div className="seg seg-wide" style={{ maxWidth: 420 }}>
                <button
                  type="button"
                  className={`seg-btn ${status === 'decided' ? 'active' : ''}`}
                  onClick={() => setStatus('decided')}
                >
                  Decided
                </button>
                <button
                  type="button"
                  className={`seg-btn ${status === 'open' ? 'active' : ''}`}
                  onClick={() => setStatus('open')}
                >
                  Open
                </button>
              </div>
              <p className="hintline">
                {status === 'decided'
                  ? 'A decided call shows green in the log; set who made it and when below.'
                  : 'Choose Decided once the call is made — it reads amber in the log until then.'}
              </p>
            </div>
          )}

          {status === 'decided' && (
            <div className="fd-sec">
              <h2 className="fd-sec-h">Who made the call, and when</h2>
              <div className="fd-grid">
                <div className="fd-field">
                  <label className="fd-lab">
                    Decided by<span className="req">*</span>
                  </label>
                  <select
                    className="input"
                    aria-required
                    value={decidedBy}
                    onChange={(e) => setDecidedBy(e.target.value)}
                  >
                    {people.map((email) => (
                      <option key={email} value={email}>
                        {displayName(email, profiles)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="fd-field">
                  <label className="fd-lab">
                    Decided on<span className="req">*</span>
                  </label>
                  <DateField
                    value={decidedOn}
                    onChange={setDecidedOn}
                    ariaLabel="Decided on"
                    ariaRequired
                    variant="form"
                  />
                </div>
              </div>
              <p className="hintline">
                Defaults to you, today — change either to record a call made earlier, or by someone
                else.
              </p>
            </div>
          )}

          {problem && <p className="hintline" role="alert">{problem}</p>}

          <div className="fd-actions">
            <button className="btn btn-primary" onClick={submit} disabled={busy}>
              {submitLabel}
            </button>
            <button className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
